import pictogramAsset from "assets/img/pictogram/asset.svg"

import React, { FC } from "react"
import { useTranslation } from "react-i18next"
import { useUnit } from "effector-react"

import { Grid, GridItem } from "components/Grid"
import { Flex } from "components/Flex"
import Text from "components/Text/Text"
import Modal from "components/Modal/Modal"
import { Guid } from "@/types"
import useArticle from "hooks/useArticle"
import Pictogram from "components/Pictogram/Pictogram"
import InfoButton from "components/InfoButton/InfoButton"
import articleSidebars from "stores/articleSidebars"
import Lmid from "components/Lmid/Lmid"
import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import { ArticleZts } from "models/ArticleZts"
import { getArticleDescriptionHeaderTextType } from "utils/utils"
import useGlobalAssetOverviewAsset from "./useGlobalAssetOverviewAsset"
import { $tenantsMap } from "stores/globalAssetOverviewTenants"
import GlobalAssetOvewviewSidebarFields from "./GlobalAssetOverviewSidebarFields"

export interface IGlobalAssetOverviewAssetSidebarProps {
    assetId: Guid
    onClose?(): void
}

const GlobalAssetOverviewAssetSidebar: FC<React.PropsWithChildren<IGlobalAssetOverviewAssetSidebarProps>> = ({
    assetId,
    onClose,
}) => {
    const { t } = useTranslation()
    const { asset } = useGlobalAssetOverviewAsset(assetId, onClose)
    const { article } = useArticle(asset?.articleId, onClose)
    const tenantsMap = useUnit($tenantsMap)
    const tenantName = asset ? tenantsMap.get(asset.hoderId)?.tenantName : undefined

    return (
        <>
            <Modal type="sidebar"
                isBusy={!asset || !article}
                title={t('asset.assetDetails')}
                onClose={onClose}
                closeButton={false}
                maxWidth="720px"
                toolbar={tenantName
                    ? <div className="text-right word-break-break-word">
                        {t('globalAssetOverview.assetOwnedBy')} <span className="font-bold">{tenantName}</span>
                    </div>
                    : null
                }>
                {!!asset && !!article &&
                    <>
                        <Grid
                            className="mb-4"
                            columns="6.4rem auto"
                            gap="2.4rem">
                            <GridItem>
                                <Pictogram src={pictogramAsset} />
                            </GridItem>
                            <GridItem>
                                <Flex alignItems="center">
                                    <Text type={getArticleDescriptionHeaderTextType(article.description)} className="mr-2">
                                        <ArticleDescription
                                            articleDescription={article.description}
                                            clamp={2}
                                        />
                                    </Text>
                                    <InfoButton
                                        onClick={() => articleSidebars.show({ articleId: asset.articleId })}
                                    />
                                </Flex>
                                {asset.altArticleDescription
                                    ? <div className="color-grey-500 mb-1">
                                        {asset.altArticleDescription}
                                    </div>
                                    : (
                                        article instanceof ArticleZts && article.descriptionOverwritten
                                            ? <div className="color-grey-500 mb-1">
                                                {t('article.notOriginalZtsDescription')}
                                            </div>
                                            : null
                                    )
                                }
                                <Grid columns="max-content minmax(0, max-content)" gap="0.8rem">
                                    <GridItem>{t('common.lmid')}:</GridItem>
                                    <GridItem>
                                        <Lmid
                                            lmid={asset.lmid}
                                            labeled={asset.isLabeled}
                                            inCollection={asset.linkedToAssetCollection}
                                        />
                                    </GridItem>
                                </Grid>
                            </GridItem>
                        </Grid>
                        <GlobalAssetOvewviewSidebarFields
                            asset={asset}
                            article={article}
                        />
                    </>
                }
            </Modal>
        </>
    )
}

export default GlobalAssetOverviewAssetSidebar
